import React from "react";
import { produce } from "immer";

import { logger } from "@jslib/shared/utils/logger";

import {
  type VerificationService,
  type StudentPersonalInfoViewModel,
  type ThirdPartyLoopResponse,
} from "../../lib/types/types";
import { VerificationStepsEnum } from "../../lib/types/runtimeTypes";
import { closeTabRef } from "../../lib/refs/refs";
import { LogoComponent } from "../LogoComponent/LogoComponent";
import { PoweredByComponent } from "../PoweredBy/PoweredByComponent";
import { FormFooterComponent as FormFooter } from "../FormFooter/FormFooterComponent";
import { usePollingInterval } from "../../lib/utils/usePollingInterval/usePollingInterval";
import {
  getPreviousStepOverride,
  getRouteOverride,
} from "../../lib/ServerApi/TestingRouteOverrides";

import { ssoMessages } from "../StepSSO/StepSSOComponent";
import { useFlag } from "../FeatureFlags/hooks";

import { ReviewPendingComponent } from "./ReviewPendingComponent";
import { IDCheckLoopPendingComponent } from "./IDCheckLoopPendingComponent";
import { ThirdPartyLoopPendingComponent } from "./ThirdPartyLoopPendingComponent";
import { SingleSignOnPendingComponent } from "./SingleSignOnPendingComponent";

// Poll fast (5s) for the first 20s after doc upload to catch quick verifications,
// then back off to 20s. Matches the historic cadence that the removed countdown
// implicitly produced (5s during the visible countdown, 20s after).
const DOC_UPLOAD_FAST_POLL_DURATION = 5000;
const DOC_UPLOAD_FAST_POLL_WINDOW = 20000;
const DOC_UPLOAD_POLL_DURATION = 20000;
const SINGLE_SIGN_ON_POLL_DURATION = 2000;

interface StepPendingProps {
  verificationService: VerificationService;
}

export const StepPendingComponent = ({ verificationService }: StepPendingProps) => {
  const { programId } = verificationService;
  const { viewModel } = verificationService;
  const previousViewModel =
    (verificationService.previousViewModel as StudentPersonalInfoViewModel) || undefined;
  const verificationLastResponse =
    verificationService.verificationResponse.lastResponse ||
    verificationService.previousVerificationResponse ||
    undefined;
  const { verificationId } = verificationService.verificationResponse;
  const isMockingStep = getRouteOverride();
  const isMockingPreviousStepSSO =
    getPreviousStepOverride() && getPreviousStepOverride() === VerificationStepsEnum.sso;
  const isMockingPreviousStepThirdPartyLoop =
    getPreviousStepOverride() && getPreviousStepOverride() === VerificationStepsEnum.thirdPartyLoop;
  const isMockingPreviousStepIdCheckLoop =
    getPreviousStepOverride() && getPreviousStepOverride() === VerificationStepsEnum.idCheckLoop;
  const shouldHideIcon = useFlag("hc-my-sheer-id-theme-builder");

  const isPreviousStepSSO = (): boolean =>
    verificationLastResponse?.currentStep === VerificationStepsEnum.sso;

  const isPreviousStepThirdPartyLoop = (): boolean =>
    verificationLastResponse?.currentStep === VerificationStepsEnum.thirdPartyLoop;

  const isPreviousStepIdCheckLoop = (): boolean =>
    verificationLastResponse?.currentStep === VerificationStepsEnum.idCheckLoop;

  const isSsoOrThirdPartyLoopPath =
    isPreviousStepSSO() ||
    isPreviousStepThirdPartyLoop() ||
    isMockingPreviousStepSSO ||
    isMockingPreviousStepThirdPartyLoop;
  const isIdCheckLoopPath = isPreviousStepIdCheckLoop() || isMockingPreviousStepIdCheckLoop;
  const isDocUploadPath = !isSsoOrThirdPartyLoopPath && !isIdCheckLoopPath;

  const [pollTime, setPollTime] = React.useState<number>(() => {
    if (isSsoOrThirdPartyLoopPath) return SINGLE_SIGN_ON_POLL_DURATION;
    if (isIdCheckLoopPath) return DOC_UPLOAD_POLL_DURATION;
    return DOC_UPLOAD_FAST_POLL_DURATION;
  });

  React.useEffect(() => {
    if (!isDocUploadPath) return undefined;
    const timeoutId = setTimeout(
      () => setPollTime(DOC_UPLOAD_POLL_DURATION),
      DOC_UPLOAD_FAST_POLL_WINDOW,
    );
    return () => clearTimeout(timeoutId);
  }, [isDocUploadPath]);

  const stopPollingInterval =
    !isMockingStep &&
    usePollingInterval(() => {
      verificationService.fetchExistingVerificationRequest(
        programId,
        verificationId,
        verificationLastResponse,
        previousViewModel,
        false,
      );
    }, pollTime);

  const cancelSingleSignOnStep = (
    step: VerificationStepsEnum.thirdPartyLoop | VerificationStepsEnum.sso,
  ) => {
    const nextState = produce(viewModel, () => {});
    logger.info("StepSingleSignOnComponent submitting cancel request, step:", step);
    stopPollingInterval();

    closeTabRef();
    verificationService.submitStep(step, nextState, verificationLastResponse);
  };

  const logo = (
    <LogoComponent
      verificationService={verificationService}
      fallbackLogo={
        !shouldHideIcon && (
          <img
            className="sid-l-lead-image"
            alt=""
            src="https://assets-resources.sheerid.com/common/images/2018/icons/doc-pending.svg"
          />
        )
      }
    />
  );

  const getPendingContent = () => {
    const showSSO = isPreviousStepSSO() || isMockingPreviousStepSSO;
    const showThirdPartyLoop =
      isPreviousStepThirdPartyLoop() || isMockingPreviousStepThirdPartyLoop;
    const showIdCheckLoop = isPreviousStepIdCheckLoop() || isMockingPreviousStepIdCheckLoop;

    if (showSSO) {
      return (
        <SingleSignOnPendingComponent
          stepName={VerificationStepsEnum.sso}
          messages={ssoMessages}
          logo={logo}
          cancelSingleSignOn={() => cancelSingleSignOnStep(VerificationStepsEnum.sso)}
        />
      );
    }

    if (showThirdPartyLoop) {
      return (
        <ThirdPartyLoopPendingComponent
          logo={logo}
          cancelThirdPartyLoop={() => cancelSingleSignOnStep(VerificationStepsEnum.thirdPartyLoop)}
          verificationLastResponse={verificationLastResponse as ThirdPartyLoopResponse}
          isMockingPreviousStepThirdPartyLoop={isMockingPreviousStepThirdPartyLoop}
        />
      );
    }

    if (showIdCheckLoop) {
      return <IDCheckLoopPendingComponent logo={logo} />;
    }

    return <ReviewPendingComponent logo={logo} />;
  };

  return (
    <div id="sid-step-pending" className="sid-l-container">
      <div role="main">{getPendingContent()}</div>
      {isIdCheckLoopPath ? (
        <PoweredByComponent verificationService={verificationService} isCentered />
      ) : (
        <FormFooter verificationService={verificationService}>
          <div /> {/* suppress default */}
        </FormFooter>
      )}
    </div>
  );
};
